/**
 * User Mentions TipTap Extension
 *
 * @ mentions functionality for users with @everyone support.
 * Fetches users from channels or groups based on context.
 */

import Mention from '@tiptap/extension-mention'
import { findSuggestionMatch, type SuggestionMatch, type Trigger } from '@tiptap/suggestion'
import { VueRenderer } from '@tiptap/vue-3'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import { Z_INDEX } from '@common/core-layer/constants'
import UserMentionsList from '../suggestions/UserMentionsList.vue'

import { userMentionsPluginKey } from './mentionPluginKeys'

// TipTap components and popup references
let component: VueRenderer | null = null
let popup: TippyInstance | null = null
let popupResizeObserver: ResizeObserver | null = null

const USER_MENTION_TERMINATOR = /[.,!?;:()[\]{}]/

// A member name spans at most six words, so anything with more spaces than this is
// prose rather than a mention and must end the session.
export const USER_MENTION_MAX_SPACES = 5

export const findUserMentionMatch = (config: Trigger): SuggestionMatch => {
  const match = findSuggestionMatch(config)

  if (!match) {
    return null
  }

  const { query } = match
  const spaceCount = query.split(' ').length - 1

  if (/^\s/.test(query) || /[\t\r\n]/.test(query) || USER_MENTION_TERMINATOR.test(query) || spaceCount > USER_MENTION_MAX_SPACES) {
    return null
  }

  return match
}

const getMentionPopupParent = () => {
  // Keep the popup inside the HighRise namespace so wrapper-scoped styles still apply.
  return document.querySelector<HTMLElement>('.membershipApp:not(.hr-detached-container)') ?? document.body
}

export const UserMentions = Mention.extend({
  name: 'userMentions',
  priority: 1000, // High priority to handle keyboard before other extensions
}).configure({
  HTMLAttributes: {
    class: 'mention',
  },
  suggestion: {
    char: '@',
    allowSpaces: true,
    findSuggestionMatch: findUserMentionMatch,
    pluginKey: userMentionsPluginKey,

    // Items function - needs to return something for keyboard navigation to work
    items: async ({ query }: { query: string }) => {
      // Return a placeholder - the component handles actual display
      // But TipTap needs items for its internal keyboard handling
      return [{ id: 'loading', label: query }]
    },

    render: () => {
      return {
        onStart(props: any) {
          component = new VueRenderer(UserMentionsList, {
            props: {
              ...props,
              // Pass query to component so it can fetch its own data
              query: props.query || '',
            },
            editor: props.editor,
          })

          if (!props.clientRect) {
            return
          }

          if (!component.element) {
            return
          }

          popup = tippy(document.body, {
            appendTo: getMentionPopupParent,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            theme: 'cp-mention-suggestions',
            arrow: false,
            maxWidth: 'none',
            getReferenceClientRect: props.clientRect as () => DOMRect,
            zIndex: Z_INDEX.MENTION_SUGGESTIONS,
            popperOptions: {
              strategy: 'fixed',
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top-start', 'bottom-start', 'right-start', 'left-start'],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                  },
                },
              ],
            },
            hideOnClick: false,
            onShow(instance) {
              // Add mobile-specific event listeners to close on outside touch
              const handleOutsideTouch = (event: Event) => {
                const target = event.target as Element
                if (!instance.popper.contains(target)) {
                  instance.hide()
                }
              }

              // Add click listener after mount cycle to avoid immediate close
              const outsideListenerTimeoutId = window.setTimeout(() => {
                document.addEventListener('click', handleOutsideTouch, {
                  passive: true,
                })
              }, 0)

              ;(instance as any)._outsideTouchHandler = handleOutsideTouch
              ;(instance as any)._outsideTouchTimeoutId = outsideListenerTimeoutId
            },
            onHide(instance) {
              if ((instance as any)._outsideTouchHandler) {
                document.removeEventListener('click', (instance as any)._outsideTouchHandler)
                delete (instance as any)._outsideTouchHandler
              }
              if ((instance as any)._outsideTouchTimeoutId) {
                window.clearTimeout((instance as any)._outsideTouchTimeoutId)
                delete (instance as any)._outsideTouchTimeoutId
              }
            },
          })

          // Re-evaluate placement when async suggestions change popup size.
          if (typeof ResizeObserver !== 'undefined' && component.element) {
            const clientRect = props.clientRect as () => DOMRect
            popupResizeObserver = new ResizeObserver(() => {
              if (!popup) {
                return
              }
              popup.setProps({
                getReferenceClientRect: clientRect,
              })
              popup.popperInstance?.update()
            })
            popupResizeObserver.observe(component.element)
          }
        },

        onUpdate(props: any) {
          if (component) {
            component.updateProps({
              ...props,
              query: props.query || '',
            })
          }

          if (!props.clientRect || !popup) {
            return
          }

          popup.setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          })
          popup.popperInstance?.update()
        },

        onBeforeUpdate() {
          return true
        },

        onKeyDown(props: any) {
          const { event } = props

          if (!component) {
            return false
          }

          const componentRef = (component as any).ref

          // Special handling for Enter - get selected item and call command
          if (event.key === 'Enter') {
            const selectedItem = componentRef?.getSelectedItem?.()
            if (selectedItem && props.command) {
              event.preventDefault()
              event.stopPropagation()
              props.command(selectedItem)
              return true
            }
          }

          if (componentRef?.handleKeyDown) {
            return componentRef.handleKeyDown(event)
          }

          return false
        },

        onExit() {
          try {
            if (component) {
              component.destroy()
              component = null
            }
          } catch (error) {
            console.warn('[UserMentions] Error destroying component:', error)
          }

          try {
            if (popupResizeObserver) {
              popupResizeObserver.disconnect()
              popupResizeObserver = null
            }
          } catch (error) {
            console.warn('[UserMentions] Error disconnecting popup observer:', error)
          }

          try {
            if (popup) {
              popup.destroy()
              popup = null
            }
          } catch (error) {
            console.warn('[UserMentions] Error removing popup:', error)
          }
        },
      }
    },

    command: ({ editor, range, props }: any) => {
      try {
        // Delete the @ and query text
        editor.chain().focus().deleteRange(range).run()

        // Insert mention using unified mention node
        if (props.type === 'broadcast') {
          // Broadcast mention (@everyone) - render as span
          editor.commands.insertMention({
            label: props.label,
            name: props.label,
            type: 'broadcast',
          })
        } else {
          // Regular user mention - render as anchor
          editor.commands.insertMention({
            id: props.id,
            label: props.label,
            name: props.label,
            slug: props.slug,
            type: 'user',
          })
        }

        // Add space after mention
        editor.commands.insertContent(' ')
      } catch (error) {
        console.error('[UserMentions] Error in command:', error)
      }
    },
  },
})
